import { CoreLoader, CoreTag } from '@featuring-corp/components';
import {
	IconInstagramColored,
	IconNaverBlogColored,
	IconTiktokColored,
	IconXColored,
	IconYoutubeColored,
} from '@featuring-corp/icons';
import { typoVariant } from '@styles/vanilla-extract/typography.css';
import {
	commonContainer,
	disabledAnchor,
	ellipsis,
	flex,
} from '@styles/vanilla-extract/utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import React, { FunctionComponent, createElement } from 'react';

import { PlatformCode } from '@type/common';

import { setProfileColor } from '@utils/profileColors';

import {
	cellClass,
	cellGrowClass,
	cellWidth,
	loadingContainer,
	rowClass,
	tableClass,
} from './style.css';
import NoDataSection from '../no-data-section/NoDataSection';
import { getTagColorType } from '../report-profile/util';
import { lackOfPermissionClass } from '../style.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CorePictureAvatar from 'src/shared/avatar/CorePictureAvatar';

type DataFromHeader<Headers extends string[]> = {
	[Header in Headers[number]]?: string;
};
export interface InfluencerTableProps<T extends string[]> {
	header?: T;
	data: ({
		platformCode?: PlatformCode;
		pk?: string;
		profileImg?: string;
		userName?: string;
		fullName?: string;
		categories?: string[];
	} & DataFromHeader<T>)[];
	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const InfluencerTable = <T extends string[]>({
	header,
	data = [],
	isLoading,
	isError,
	lackOfPermission,
}: InfluencerTableProps<T>) => {
	const { t } = useTranslation('commonInfReport');
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const platformCode = searchParams.get('p') as PlatformCode;

	const platformIconMapper: Record<PlatformCode, FunctionComponent<{ size: number }>> = {
		ig: IconInstagramColored,
		yt: IconYoutubeColored,
		nb: IconNaverBlogColored,
		tt: IconTiktokColored,
		// tw: IconXColored,
	};
	const available = !isLoading && !isError && !lackOfPermission;

	const handleClickReportItem = (platformCode?: PlatformCode, pk?: string) => {
		if (!platformCode || !pk) return;

		const params = new URLSearchParams(location.search);
		params.delete('rm');
		params.delete('pk');
		params.delete('p');
		params.append('rm', 'm');
		params.append('pk', pk);
		params.append('p', platformCode);

		const newUrl = `${location.pathname}?${params.toString()}`;

		window?.open(newUrl);
	};

	return (
		<table className={tableClass}>
			<th className={rowClass}>
				<td
					className={cellClass()}
					style={assignInlineVars({ [cellWidth]: '88px' })}
				></td>
				<td className={cellClass()} style={assignInlineVars({ [cellWidth]: '192px' })}>
					{t('features.report.influencer-table.header.id')}
				</td>
				<td className={clsx(cellClass(), cellGrowClass)}>
					{t('features.report.influencer-table.header.category')}
				</td>
				{header?.map((header) => (
					<td
						key={header}
						className={cellClass({ align: 'right' })}
						style={assignInlineVars({ [cellWidth]: '100px' })}
					>
						{header}
					</td>
				))}
			</th>

			{available &&
				data.length > 0 &&
				data.map((item, idx) => (
					<tr
						key={idx}
						className={rowClass}
						onClick={() => handleClickReportItem(item.platformCode, item.pk)}
					>
						<td className={cellClass()} style={assignInlineVars({ [cellWidth]: '88px' })}>
							<div className={flex({ gap: 'spacing-400' })}>
								{createElement(platformIconMapper[item.platformCode ?? 'ig'], {
									size: 24,
								})}
								<CorePictureAvatar
									picture={item.profileImg}
									color_type={setProfileColor(item.pk ?? '0')}
									text={item.userName ?? ''}
									size="small"
								/>
							</div>
						</td>
						<td
							className={cellClass()}
							style={assignInlineVars({ [cellWidth]: '192px' })}
						>
							<div className={flex({ direction: 'column', align: 'start' })}>
								<p className={clsx(typoVariant.heading[2], ellipsis)}>{item.userName}</p>
								<p className={clsx(typoVariant.body[1], ellipsis)}>{item.fullName}</p>
							</div>
						</td>
						<td className={clsx(cellClass(), cellGrowClass)}>
							<div className={flex({ gap: 'spacing-200' })}>
								{item.categories?.map((category, idx) => (
									<CoreTag
										key={idx}
										text={category}
										tagType={getTagColorType(category)}
									/>
								))}
							</div>
						</td>
						{header?.map((headerItem) => (
							<td
								key={headerItem}
								className={cellClass({ align: 'right' })}
								style={assignInlineVars({ [cellWidth]: '100px' })}
							>
								{item?.[headerItem as (typeof header)[number]] ?? '-'}
							</td>
						))}
					</tr>
				))}

			{available && (data.length === 0 || !data) && <NoDataSection />}

			{isLoading && (
				<div className={loadingContainer}>
					<CoreLoader size="sm" />
				</div>
			)}

			{lackOfPermission &&
				new Array(5).fill(0).map((_, idx) => (
					<tr key={idx} className={clsx(rowClass, lackOfPermissionClass)}>
						<td className={cellClass()} style={assignInlineVars({ [cellWidth]: '88px' })}>
							<div className={flex({ gap: 'spacing-400' })}>
								{createElement(platformIconMapper[platformCode], { size: 24 })}
								<CorePictureAvatar color_type={'green'} text={'e'} size="small" />
							</div>
						</td>
						<td
							className={cellClass()}
							style={assignInlineVars({ [cellWidth]: '192px' })}
						>
							<div className={flex({ direction: 'column', align: 'start' })}>
								<p className={clsx(typoVariant.heading[2], ellipsis)}>{'example'}</p>
								<p className={clsx(typoVariant.body[1], ellipsis)}>{'example'}</p>
							</div>
						</td>
						<td className={clsx(cellClass(), cellGrowClass)}>
							<div className={flex({ gap: 'spacing-200' })}>
								<CoreTag
									key={idx}
									text={'category'}
									tagType={getTagColorType('category')}
								/>
							</div>
						</td>

						<td
							className={cellClass({ align: 'right' })}
							style={assignInlineVars({ [cellWidth]: '100px' })}
						>
							example
						</td>
						<td
							className={cellClass({ align: 'right' })}
							style={assignInlineVars({ [cellWidth]: '100px' })}
						>
							example
						</td>
					</tr>
				))}

			{/* {isLoading && new Array(5).fill(0).map((_, idx) => <TableSkeleton key={idx} />)} */}
		</table>
	);
};

export default InfluencerTable;
